/**
 * Per-row health rail tone mapping — shared across master-list verticals.
 *
 * Each list (customers, vendors, …) drives a 3px coloured stripe down the
 * left edge of every row, keyed off a per-record health signal. The mapping
 * is encoded here once so adding a tone (e.g. `recurring` for vendors that
 * the customer list doesn't have) lights up the same way wherever the
 * shared row component renders.
 *
 * All colours come from the brand spec; no fresh palette.
 */
import { cn } from "@flint/ui";

export type RailTone =
  | "none" // hover-only (no signal yet)
  | "late" // bad / overdue
  | "warn" // amber / archived / awaiting
  | "flag" // bad / awaiting founder approval — vendors only
  | "good" // ok / on-time
  | "info" // sky / informational
  | "recurring"; // slate-700 / vendors only

interface RailStyle {
  bar: string;
  /** True = always-visible (risky); false = visible on hover only. */
  alwaysOn: boolean;
}

const STYLES: Record<RailTone, RailStyle> = {
  none: { bar: "bg-line-2", alwaysOn: false },
  late: { bar: "bg-bad", alwaysOn: true },
  flag: { bar: "bg-bad", alwaysOn: true },
  warn: { bar: "bg-warn", alwaysOn: true },
  good: { bar: "bg-ok", alwaysOn: false },
  info: { bar: "bg-sky-600", alwaysOn: false },
  recurring: { bar: "bg-slate-700", alwaysOn: false },
};

export function railClass(tone: RailTone): string {
  const s = STYLES[tone];
  return cn(
    "absolute left-0 top-0 bottom-0 w-[3px] transition-opacity",
    s.bar,
    s.alwaysOn ? "opacity-100" : "opacity-0 group-hover:opacity-100",
  );
}
